import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { takeReturnTo } from "../auth/cognito";

/**
 * Cognito Hosted UI bounces here with ?code=... after sign-in (email/password
 * or Google — both go through the Hosted UI now). We exchange the code for
 * tokens at the Cognito token endpoint, stash them in localStorage in the shape
 * amazon-cognito-identity-js expects, then return the user to where they were
 * headed (see startHostedSignIn / takeReturnTo in auth/cognito.ts).
 */
export function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();

  useEffect(() => {
    const code = params.get("code");
    if (!code) {
      nav("/", { replace: true });
      return;
    }

    const exchange = async () => {
      const domain = import.meta.env.VITE_COGNITO_DOMAIN as string;
      const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
      const redirect = `${import.meta.env.VITE_PUBLIC_BASE_URL}/auth/callback`;

      const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: clientId,
        code,
        redirect_uri: redirect,
      });

      const resp = await fetch(`https://${domain}/oauth2/token`, {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!resp.ok) {
        nav("/signin?error=oauth", { replace: true });
        return;
      }
      const tokens = await resp.json();
      storeCognitoTokens(clientId, tokens);
      await refresh();
      nav(takeReturnTo(), { replace: true });
    };

    exchange();
  }, [params, nav, refresh]);

  return <div style={{ padding: 48, textAlign: "center", color: "var(--ink-soft)" }}>Signing you in…</div>;
}

interface TokenResp {
  id_token: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

// Write tokens into localStorage in the format amazon-cognito-identity-js expects
// so the rest of the app sees the user as signed in via the SDK.
function storeCognitoTokens(clientId: string, tokens: TokenResp): void {
  const idPayload = decodeJwt(tokens.id_token);
  const username = (idPayload["cognito:username"] as string) || (idPayload.sub as string);
  const prefix = `CognitoIdentityServiceProvider.${clientId}`;
  localStorage.setItem(`${prefix}.LastAuthUser`, username);
  localStorage.setItem(`${prefix}.${username}.idToken`, tokens.id_token);
  localStorage.setItem(`${prefix}.${username}.accessToken`, tokens.access_token);
  localStorage.setItem(`${prefix}.${username}.refreshToken`, tokens.refresh_token);
  localStorage.setItem(`${prefix}.${username}.clockDrift`, "0");
}

function decodeJwt(jwt: string): Record<string, unknown> {
  const part = jwt.split(".")[1];
  if (!part) return {};
  const padded = part.replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(padded));
}
